import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormDialog } from "@/components/admin/FormDialog";
import { DictionaryForm } from "@/components/admin/DictionaryForm";
import { DictionaryItemForm } from "@/components/admin/DictionaryItemForm";
import { Dictionary, DictionaryFormValues, DictionaryItem, DictionaryItemFormValues } from "@/types/dictionary";
import { api } from "@/lib/api";
import { Loader2, Plus, Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

const dictionarySchema = z.object({
  name: z.string().min(1, "Название обязательно"),
  code: z.string().min(1, "Код обязателен"),
  description: z.string().optional(),
});

const dictionaryItemSchema = z.object({
  name: z.string().min(1, "Название обязательно"),
  code: z.string().min(1, "Код обязателен"),
  description: z.string().optional(),
  is_active: z.boolean().default(true),
});

export default function DictionariesAdmin() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [editingDictionary, setEditingDictionary] = useState<Dictionary | null>(null);
  const [editingItem, setEditingItem] = useState<DictionaryItem | null>(null);
  const [selectedDictionary, setSelectedDictionary] = useState<Dictionary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedDictionaries, setExpandedDictionaries] = useState<Set<string>>(new Set());

  const dictionaryForm = useForm<DictionaryFormValues>({
    resolver: zodResolver(dictionarySchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
    },
    mode: "onChange"
  });

  const dictionaryItemForm = useForm<DictionaryItemFormValues>({
    resolver: zodResolver(dictionaryItemSchema),
    defaultValues: {
      name: "",
      code: "",
      description: "",
      is_active: true
    },
    mode: "onChange"
  });

  const { data: dictionaries, isLoading, error } = useQuery({
    queryKey: ["dictionaries"],
    queryFn: api.dictionaries.getAll,
  });

  const handleCreateDictionary = async (data: DictionaryFormValues) => {
    console.log("Creating dictionary with data:", data);
    try {
      setIsSubmitting(true);
      const result = await api.dictionaries.create(data);
      console.log("Create result:", result);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      setIsCreateDialogOpen(false);
      dictionaryForm.reset();
      toast.success("Справочник успешно создан");
    } catch (error) {
      console.error("Error creating dictionary:", error);
      toast.error("Ошибка при создании справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateDictionary = async (data: DictionaryFormValues) => {
    if (!editingDictionary) return;
    try {
      setIsSubmitting(true);
      await api.dictionaries.update(editingDictionary.id, data);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      setIsEditDialogOpen(false);
      setEditingDictionary(null);
      dictionaryForm.reset();
      toast.success("Справочник успешно обновлен");
    } catch (error) {
      console.error("Error updating dictionary:", error);
      toast.error("Ошибка при обновлении справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDictionary = async (id: string) => {
    try {
      setIsSubmitting(true);
      await api.dictionaries.delete(id);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      toast.success("Справочник успешно удален");
    } catch (error) {
      console.error("Error deleting dictionary:", error);
      toast.error("Ошибка при удалении справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateItem = async (data: DictionaryItemFormValues) => {
    if (!selectedDictionary) return;
    try {
      setIsSubmitting(true);
      await api.dictionaries.createItem(selectedDictionary.id, data);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      setIsItemDialogOpen(false);
      dictionaryItemForm.reset();
      toast.success("Элемент справочника успешно создан");
    } catch (error) {
      console.error("Error creating dictionary item:", error);
      toast.error("Ошибка при создании элемента справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateItem = async (data: DictionaryItemFormValues) => {
    if (!editingItem || !selectedDictionary) return;
    try {
      setIsSubmitting(true);
      await api.dictionaries.updateItem(selectedDictionary.id, editingItem.id, data);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      setIsItemDialogOpen(false);
      setEditingItem(null);
      dictionaryItemForm.reset();
      toast.success("Элемент справочника успешно обновлен");
    } catch (error) {
      console.error("Error updating dictionary item:", error);
      toast.error("Ошибка при обновлении элемента справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (dictionaryId: string, itemId: string) => {
    try {
      setIsSubmitting(true);
      await api.dictionaries.deleteItem(dictionaryId, itemId);
      await queryClient.invalidateQueries({ queryKey: ["dictionaries"] });
      toast.success("Элемент справочника успешно удален");
    } catch (error) {
      console.error("Error deleting dictionary item:", error);
      toast.error("Ошибка при удалении элемента справочника");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleDictionaryExpansion = (dictionaryId: string) => {
    const newExpanded = new Set(expandedDictionaries);
    if (newExpanded.has(dictionaryId)) {
      newExpanded.delete(dictionaryId);
    } else {
      newExpanded.add(dictionaryId);
    }
    setExpandedDictionaries(newExpanded);
  };

  const filteredDictionaries = dictionaries?.filter(dictionary =>
    dictionary.name.toLowerCase().includes(search.toLowerCase()) ||
    dictionary.code.toLowerCase().includes(search.toLowerCase())
  ) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500">
        Ошибка при загрузке справочников
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Input
          placeholder="Поиск справочников..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
        <Button 
          onClick={() => {
            console.log("Opening create dialog");
            setIsCreateDialogOpen(true);
          }}
          disabled={isSubmitting}
        >
          <Plus className="mr-2 h-4 w-4" />
          Создать справочник
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead>Название</TableHead>
            <TableHead>Код</TableHead>
            <TableHead>Описание</TableHead>
            <TableHead>Количество элементов</TableHead>
            <TableHead className="w-[100px]">Действия</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredDictionaries.map((dictionary) => (
            <>
              <TableRow key={dictionary.id}>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleDictionaryExpansion(dictionary.id)}
                    className="h-8 w-8 p-0"
                  >
                    {expandedDictionaries.has(dictionary.id) ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                </TableCell>
                <TableCell>{dictionary.name}</TableCell>
                <TableCell>{dictionary.code}</TableCell>
                <TableCell>{dictionary.description}</TableCell>
                <TableCell>{dictionary.items.length}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setSelectedDictionary(dictionary);
                        setIsItemDialogOpen(true);
                      }}
                      disabled={isSubmitting}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingDictionary(dictionary);
                        setIsEditDialogOpen(true);
                      }}
                      disabled={isSubmitting}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteDictionary(dictionary.id)}
                      disabled={isSubmitting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
              {expandedDictionaries.has(dictionary.id) && dictionary.items.length > 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="pl-8">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Название элемента</TableHead>
                            <TableHead>Код элемента</TableHead>
                            <TableHead>Описание</TableHead>
                            <TableHead>Статус</TableHead>
                            <TableHead className="w-[100px]">Действия</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dictionary.items.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.code}</TableCell>
                              <TableCell>{item.description}</TableCell>
                              <TableCell>{item.is_active ? "Активен" : "Неактивен"}</TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setSelectedDictionary(dictionary);
                                      setEditingItem(item);
                                      setIsItemDialogOpen(true);
                                    }}
                                    disabled={isSubmitting}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleDeleteItem(dictionary.id, item.id)}
                                    disabled={isSubmitting}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </>
          ))}
        </TableBody>
      </Table>

      <FormDialog
        title="Создание справочника"
        isOpen={isCreateDialogOpen}
        onClose={() => {
          console.log("Closing create dialog");
          setIsCreateDialogOpen(false);
          dictionaryForm.reset();
        }}
        onSubmit={handleCreateDictionary}
        form={dictionaryForm}
        isSubmitting={isSubmitting}
      >
        <DictionaryForm />
      </FormDialog>

      <FormDialog
        title="Редактирование справочника"
        isOpen={isEditDialogOpen}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingDictionary(null);
          dictionaryForm.reset();
        }}
        onSubmit={handleUpdateDictionary}
        form={dictionaryForm}
        isSubmitting={isSubmitting}
      >
        <DictionaryForm defaultValues={editingDictionary} />
      </FormDialog>

      <FormDialog
        title={editingItem ? "Редактирование элемента" : "Создание элемента"}
        isOpen={isItemDialogOpen}
        onClose={() => {
          setIsItemDialogOpen(false);
          setEditingItem(null);
          setSelectedDictionary(null);
          dictionaryItemForm.reset();
        }}
        onSubmit={editingItem ? handleUpdateItem : handleCreateItem}
        form={dictionaryItemForm}
        isSubmitting={isSubmitting}
      >
        <DictionaryItemForm defaultValues={editingItem} />
      </FormDialog>
    </div>
  );
} 